import React from "react";
import {
  Button,
  Input,
  Menu,
  MenuItem,
  MenuDivider,
  MenuList,
  MenuPopover,
  MenuTrigger,
  Textarea,
  Field,
  MenuItemCheckbox,
  MenuItemRadio,
} from "@fluentui/react-components";
import { DatePicker } from "@fluentui/react-datepicker-compat";
import { ChevronDownIcon } from "@fluentui/react-icons-mdl2";
import {
  CheckboxCheckedFilled,
  CheckboxUncheckedRegular,
} from "@fluentui/react-icons";
import moment from "moment";
import { ReportParameter } from "../../../types/reportTypes";
import DynamicDatePicker from "../../../components/ControlledDatePicker/dynamicDatepicker";
import DateRangePicker from "../../../components/DateRangePicker";
import { reportDateOptions } from "../../../shared/reportDateOptions";
import { resolveConfiguredPresets } from "../../../shared/dateRangePresets";

interface FormFieldProps {
  param: ReportParameter;
  value: any;
  error: string;
  loading: boolean;
  dropdownOptions: any[];
  onInputChange: (paramLabel: string, value: any) => void;
  onBlur: (paramLabel: string) => void;
  onOpenPopup: (paramLabel: string) => void;
}

const FormField: React.FC<FormFieldProps> = ({
  param,
  value,
  error,
  loading,
  dropdownOptions,
  onInputChange,
  onBlur,
  onOpenPopup,
}) => {
  const onFormatDate = (date?: Date): string =>
    date ? moment(date).format("DD-MM-YYYY") : "";

  // Resolved once per configuration rather than on every render: FormField
  // re-renders on every change to the form, and rebuilding this array would
  // hand DateRangePicker a new `presets` reference each time (and re-emit
  // resolveConfiguredPresets' warnings for a misconfigured report).
  const dateRangePresets = React.useMemo(
    () => resolveConfiguredPresets(param.presets, reportDateOptions),
    [param.presets]
  );

  const renderSelectField = () => {
    if (param.popup) {
      return (
        <Button onClick={() => onOpenPopup(param.label)}>
          {(() => {
            if (!value) return param.placeholder;

            const attributes = param.apiEndpoint?.attributes || [];
            const displayValue = param.apiEndpoint?.displayValue;

            // Helper to build name using displayValue if provided
            const getName = (item: any): string => {
              if (displayValue && item) {
                // Split by comma and trim
                const fields = displayValue
                  .split(",")
                  .map((f: any) => f.trim());
                const parts = fields
                  .map((field: any) => item[field])
                  .filter(Boolean);
                return parts.join(" ");
              }

              // Fallback: use attributes
              if (attributes.length > 0) {
                return attributes
                  .map((attr: any) => item[attr.value])
                  .filter(Boolean)
                  .join(" ");
              }

              // Final fallback
              return item.label || String(item);
            };

            if (param.multiSelect && Array.isArray(value)) {
              if (value.length === 0) return param.placeholder;

              const names = value.map(getName).filter(Boolean);

              if (names.length === 0) return param.placeholder;
              if (names.length <= 2) {
                return names.join(", ");
              } else {
                return `${names[0]}, ${names[1]} + ${names.length - 2} more`;
              }
            } else if (value && typeof value === "object") {
              return getName(value) || param.placeholder;
            } else if (typeof value === "string" && value) {
              return value;
            }

            return param.placeholder;
          })()}
        </Button>
      );
    } else {
      return (
        <Menu
          checkedValues={
            param?.multiSelect
              ? { [param?.label]: value?.map((v: any) => v.value) }
              : { [param?.label]: [value?.value] }
          }
          onCheckedValueChange={(_, { name, checkedItems }) => {
            if (param.multiSelect) {
              // ✅ Multi-select case
              const selectedOptions = dropdownOptions.filter((option: any) =>
                checkedItems.includes(option.value),
              );
              onInputChange(param.label, selectedOptions);
            } else {
              // ✅ Single-select case
              const selectedOption = dropdownOptions.find(
                (option: any) => option.value === checkedItems[0],
              );
              onInputChange(param.label, selectedOption || null);
            }
          }}
          onOpenChange={(_, { open }) => !open && onBlur(param.label)}
        >
          <MenuTrigger disableButtonEnhancement>
            <Button
              appearance="outline"
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              {(() => {
                if (loading) return "Loading...";
                if (!dropdownOptions?.length) return "No options available";

                const attributes = param.apiEndpoint?.attributes || [];
                const displayValue = param.apiEndpoint?.displayValue;

                const getName = (item: any): string => {
                  if (item.label) return item.label;

                  if (displayValue && typeof displayValue === "string") {
                    const fields = displayValue.split(",").map((f) => f.trim());
                    const parts = fields.map((f) => item[f]).filter(Boolean);
                    if (parts.length) return parts.join(" ");
                  }

                  if (attributes.length > 0) {
                    return attributes
                      .map((attr: any) => item[attr.value])
                      .filter(Boolean)
                      .join(" ");
                  }

                  return String(item.value ?? item);
                };

                if (param.multiSelect && Array.isArray(value)) {
                  if (value.length === 0)
                    return param.placeholder || "Select options";

                  const names = value.map(getName).filter(Boolean);
                  if (names.length <= 2) {
                    return names.join(", ");
                  } else {
                    return `${names[0]}, ${names[1]}, + ${names.length - 2}`;
                  }
                } else if (typeof value === "object" && value) {
                  return (
                    getName(value) || param.placeholder || "Select an option"
                  );
                } else if (typeof value === "string") {
                  return value;
                }

                return param.placeholder || "Select an option";
              })()}
              <ChevronDownIcon style={{ marginLeft: "8px" }} />
            </Button>
          </MenuTrigger>

          <MenuPopover style={{ width: 400, maxHeight: "50vh" }}>
            <MenuList>
              {param.multiSelect &&
                param.selectAll &&
                dropdownOptions?.length > 0 && (
                  <>
                    <MenuItem
                      persistOnClick
                      onClick={() => {
                        const allSelected =
                          Array.isArray(value) &&
                          value.length === dropdownOptions.length;
                        onInputChange(
                          param.label,
                          allSelected ? [] : dropdownOptions,
                        );
                      }}
                    >
                      {Array.isArray(value) &&
                      value.length === dropdownOptions.length
                        ? "Clear All"
                        : "Select All"}
                    </MenuItem>
                    <MenuDivider />
                  </>
                )}
              {dropdownOptions?.length ? (
                dropdownOptions.map((option, index) => {
                  if (!param.multiSelect) {
                    return (
                      <MenuItemRadio
                        key={option.value || index}
                        name={param.label}
                        value={option.value}
                      >
                        {option.label}
                      </MenuItemRadio>
                    );
                  }

                  // MenuItemCheckbox's default checkmark is a 16x16 slot
                  // that's always present but only visible (via CSS) once
                  // checked, so an unchecked option shows no box at all.
                  // Swapping in an explicit checkbox icon — visible whether
                  // checked or not — is what actually reads as a checkbox.
                  const checked =
                    Array.isArray(value) &&
                    value.some((v: any) => v.value === option.value);

                  return (
                    <MenuItemCheckbox
                      key={option.value || index}
                      name={param.label}
                      value={option.value}
                      checkmark={null}
                      icon={
                        checked ? (
                          <CheckboxCheckedFilled fontSize={16} />
                        ) : (
                          <CheckboxUncheckedRegular fontSize={16} />
                        )
                      }
                    >
                      {option.label}
                    </MenuItemCheckbox>
                  );
                })
              ) : (
                <MenuItem disabled>No options available</MenuItem>
              )}
            </MenuList>
          </MenuPopover>
        </Menu>
      );
    }
  };

  const renderField = () => {
    switch (param.category) {
      case "select":
        return renderSelectField();

      case "datepicker":
        return (
          <DynamicDatePicker
            border
            name={param.label}
            label={null}
            required={false}
            disabled={false}
            formik={{
              values: {
                [param.label]: value ? new Date(value) : null,
              },
              setFieldValue: (_name: string, date: Date | null) => {
                onInputChange(
                  param.label,
                  date
                    ? moment(date).format(
                        param.validate?.format || "YYYY-MM-DD",
                      )
                    : "",
                );
              },
              setFieldTouched: () => {
                onBlur(param.label);
              },
              setFieldError: () => {},
              errors: {},
              touched: {},
            }}
          />
        );

      case "daterange":
        return (
          <DateRangePicker
            presets={dateRangePresets}
            value={value || null}
            onChange={(range) => onInputChange(param.label, range)}
            placeholder={param.placeholder}
            format={param.validate?.format || "YYYY-MM-DD"}
          />
        );

      case "timepicker":
        return (
          <Input
            type="time"
            value={value || ""}
            onChange={(e) => onInputChange(param.label, e.target.value)}
            onBlur={() => onBlur(param.label)}
            placeholder="HH:mm"
          />
        );

      case "textarea":
        return (
          <Textarea
            value={value || ""}
            onChange={(e) => onInputChange(param.label, e.target.value)}
            onBlur={() => onBlur(param.label)}
          />
        );

      case "number":
        return (
          <Input
            type="number"
            value={value || ""}
            onChange={(e) => onInputChange(param.label, e.target.value)}
            onBlur={() => onBlur(param.label)}
          />
        );

      case "text":
        return (
          <Input
            value={value || ""}
            onChange={(e) => onInputChange(param.label, e.target.value)}
            onBlur={() => onBlur(param.label)}
          />
        );

      default:
        return null;
    }
  };

  return (
    <Field label={param?.label || ""} validationMessage={error}>
      {renderField()}
    </Field>
  );
};

export default FormField;
